/**
 * DECRQM (DEC Request Mode) private-mode response helper.
 *
 * WHY THIS EXISTS — opencode's TUI (opentui) freezing the pane:
 * opentui probes private DEC modes at startup via DECRQM `CSI ? <mode> $ p`
 * (it queries 2026, 2004, 1004, 1016, 2027, 2031 …). xterm.js 6.0.0's built-in
 * `requestMode` handler answers these, but in our Vite-rebundled SPA bundle it
 * throws `ReferenceError: n is not defined` — the second-pass minification of
 * the already-minified `xterm.mjs` drops the local `n` (decPrivateModes)
 * declaration while leaving its use-sites (`n.synchronizedOutput`,
 * `n.bracketedPasteMode`, `n.sendFocus`). The throw propagates out of
 * `_innerWrite`, so every byte after the probe in that write chunk — the entire
 * alt-screen paint — is dropped and the pane freezes on the bash prompt. (Raw
 * PTYs never send these probes through an emulator, which is why opencode
 * renders fine outside the browser.)
 *
 * THE FIX: TerminalTile registers a custom CSI handler for private DECRQM that
 * answers the query ourselves and returns `true`, so xterm's broken built-in
 * `requestMode` never runs.
 *
 * DECRPM reply format: `CSI ? <mode> ; <status> $ y`
 *   status 0 = not recognized, 1 = set, 2 = reset, 3 = perm set, 4 = perm reset.
 * Synchronized output (2026) IS supported by xterm 6's renderer — only the
 * QUERY responder is broken — so we report it reset/off (2), which lets opentui
 * keep using sync output (xterm renders the balanced `?2026h`/`?2026l` blocks
 * correctly). Every other private mode is reported "not recognized" (0): a safe
 * fallback that never claims support for a feature we haven't validated over
 * the ttyd→proxy→xterm transport.
 */

export const SYNCHRONIZED_OUTPUT_MODE = 2026;

/** DECRPM status code for `Ps` in `CSI ? <mode> ; <Ps> $ y`. */
const STATUS_NOT_RECOGNIZED = 0;
const STATUS_RESET = 2; // recognized + supported, currently off

/**
 * Build the DECRPM reply for a private DECRQM query on `mode`.
 * Non-numeric / missing modes are treated as 0 (the parser default).
 */
export function decrqmPrivateResponse(mode: number): string {
  const m = typeof mode === 'number' && Number.isFinite(mode) ? mode : 0;
  const status = m === SYNCHRONIZED_OUTPUT_MODE ? STATUS_RESET : STATUS_NOT_RECOGNIZED;
  return `\x1b[?${m};${status}$y`;
}
